// steps/CompanyStep.js
import React, { useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import styled from "styled-components";
import Select from "react-select";

import {
  getFieldsForProject,
  getFieldMeta,
  projectSupportsBilling,
} from "../config/companyFields";
import { createCompanySchema } from "../validators/company";
import { useAddressOptions } from "../../../hook/Subscription/useAddressOptions";
import useCheckBillingIdentity from "../../../hook/Subscription/useCheckBillingIdentity";
import { useVerifyTaxId } from "../../../hook/Subscription/useVerifyTaxId";
// import { useVerifyTaxId } from "../../../hook/Subscription/useVerifyTaxId";

const THAILAND_COUNTRY_ID = "204";

// ─── react-select style config (match form's styled-components look) ───
const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    fontSize: 14,
    backgroundColor: state.isDisabled ? "#f9fafb" : "#ffffff",
    borderColor: hasError
      ? "#ef4444"
      : state.isFocused
      ? "#6366f1"
      : "#d1d5db",
    borderRadius: 8,
    boxShadow: state.isFocused
      ? hasError
        ? "0 0 0 3px rgba(239, 68, 68, 0.1)"
        : "0 0 0 3px rgba(99, 102, 241, 0.1)"
      : "none",
    "&:hover": {
      borderColor: hasError ? "#ef4444" : state.isFocused ? "#6366f1" : "#9ca3af",
    },
    cursor: state.isDisabled ? "not-allowed" : "default",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  singleValue: (base, state) => ({
    ...base,
    color: state.isDisabled ? "#6b7280" : "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: 14 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: "0 8px",
    color: state.isDisabled ? "#d1d5db" : "#6b7280",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    zIndex: 20,
  }),
  menuList: (base) => ({ ...base, maxHeight: 240, padding: 4 }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    padding: "8px 12px",
    borderRadius: 6,
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? "#6366f1"
      : state.isFocused
      ? "#eef2ff"
      : "transparent",
    color: state.isSelected ? "#ffffff" : "#111827",
    "&:active": {
      backgroundColor: state.isSelected ? "#4f46e5" : "#e0e7ff",
    },
  }),
  noOptionsMessage: (base) => ({ ...base, fontSize: 13, color: "#9ca3af" }),
});

// ─── Helpers ────────────────────────────────
const toRsOption = (opt) => ({ value: String(opt.value), label: opt.label });
const findRsOption = (list, value) =>
  list.find((o) => o.value === String(value)) ?? null;

/**
 * Step 1 — Company information.
 */
function CompanyStep({ id_project, defaultValues = {}, onSubmit, lang = "th" }) {
  const schema = useMemo(() => createCompanySchema(id_project), [id_project]);
  const fields = useMemo(() => getFieldsForProject(id_project), [id_project]);
  const showBilling = projectSupportsBilling(id_project);

  const { province, district, subDistrict, country } = useAddressOptions({ lang });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      detail_type: "company",
      is_branch_open: false,
      name_branch: "",
      tax_number: "",
      address2: "",
      id_country: THAILAND_COUNTRY_ID,
      id_province: "",
      id_district: "",
      id_subdistrict: "",
      postal_code: "",
      ...defaultValues,
    },
    mode: "onBlur",
  });

  const detailType = watch("detail_type");
  const isPersonal = detailType === "personal";
  const isBranchOpen = watch("is_branch_open");

  // ตรวจเลขผู้เสียภาษีระหว่างพิมพ์ (debounce) — ผู้ใช้จะเห็นทันทีว่าเลขใช้ได้ไหม
  // แทนที่จะไปรู้ตอนกดสมัครแล้วโดนปฏิเสธ
  const taxNumber = watch("tax_number");
  const taxCheck = useVerifyTaxId({
    tax_number: taxNumber,
    detail_type: detailType,
    enabled: showBilling,
  });

  const idCountry = watch("id_country");
  const idProvince = watch("id_province");
  const idDistrict = watch("id_district");
  const idSubdistrict = watch("id_subdistrict");
  const isThailand = idCountry === THAILAND_COUNTRY_ID;

  // ─── Billing identity duplicate check ───
  //
  // Triggered manually inside handleFinalSubmit — see below. Holds its
  // own loading / error state so the Submit button can reflect both
  // schema validation and server-side duplicate state.
  const billingCheck = useCheckBillingIdentity();

  // ─── Memoized react-select options ───
  const countryOptions = useMemo(
    () => (country.data ?? []).map(toRsOption),
    [country.data]
  );

  const provinceOptions = useMemo(
    () => (province.data ?? []).map(toRsOption),
    [province.data]
  );

  const districtOptions = useMemo(() => {
    const all = district.data ?? [];
    const filtered = idProvince
      ? all.filter((d) => String(d.ProvinceID) === String(idProvince))
      : [];
    return filtered.map(toRsOption);
  }, [district.data, idProvince]);

  const subdistrictOptions = useMemo(() => {
    const all = subDistrict.data ?? [];
    const filtered = idDistrict
      ? all.filter((s) => String(s.DistrictID) === String(idDistrict))
      : [];
    return filtered.map(toRsOption);
  }, [subDistrict.data, idDistrict]);

  // ─── Cascade reset ───
  useEffect(() => {
    setValue("id_district", "");
    setValue("id_subdistrict", "");
    setValue("postal_code", "");
  }, [idProvince, setValue]);

  useEffect(() => {
    setValue("id_subdistrict", "");
    setValue("postal_code", "");
  }, [idDistrict, setValue]);

  // ─── Auto-fill postal_code when subdistrict selected ───
  useEffect(() => {
    if (!idSubdistrict) return;
    const all = subDistrict.data ?? [];
    const found = all.find((s) => String(s.value) === String(idSubdistrict));
    if (found?.post_code) {
      setValue("postal_code", found.post_code);
    }
  }, [idSubdistrict, subDistrict.data, setValue]);

  // ─── Reset Thailand-only fields when country changes ───
  useEffect(() => {
    if (!isThailand) {
      setValue("id_province", "");
      setValue("id_district", "");
      setValue("id_subdistrict", "");
      setValue("postal_code", "");
    }
  }, [isThailand, setValue]);

  // ─── Auto-reset branch when switching to personal ───
  useEffect(() => {
    if (isPersonal) {
      setValue("is_branch_open", false);
      setValue("name_branch", "");
    }
  }, [isPersonal, setValue]);

  const handleFinalSubmit = async (formValues) => {
    const { is_branch_open, name_branch, ...rest } = formValues;
    const payload = { ...rest };
    if (showBilling && is_branch_open && name_branch) {
      payload.name_branch = name_branch;
    }

    // เลขผู้เสียภาษีต้องผ่านก่อนไป step ถัดไป — ถ้าปล่อยผ่านจะไปโดนปฏิเสธ
    // ตอนกดจ่ายเงิน (backend superRefine) ซึ่งผู้ใช้กรอกอย่างอื่นไปหมดแล้ว
    //
    // เรียก verify() สดแทนที่จะอ่าน taxCheck.valid เฉยๆ เพราะผู้ใช้อาจกด
    // เร็วกว่า debounce (600ms) — ตอนนั้น state ยังเป็น null
    if (showBilling) {
      const taxOk = await taxCheck.verify();
      if (!taxOk) return;
    }

    // Server-side duplicate check on the billing identity. Skip when
    // the project doesn't ask for billing fields at all (the form
    // wouldn't have collected them anyway). Branch resolution mirrors
    // backend default ("00000" = HQ).
    if (showBilling) {
      const branchForCheck = isPersonal
        ? undefined
        : is_branch_open
        ? name_branch || ""
        : "00000";

      const result = await billingCheck.check({
        id_project,
        detail_type: formValues.detail_type,
        tax_number: formValues.tax_number,
        name_branch: branchForCheck,
      });

      // Stop submission if the server reports a duplicate — error
      // message renders below via billingCheck state. We don't try to
      // map it onto a specific RHF field error; a clear inline message
      // near the duplicated input is more honest.
      if (!result.ok) return;
    }

    onSubmit(payload);
  };

  const taxLabel = isPersonal
    ? lang === "en"
      ? "National ID / Passport"
      : "เลขบัตรประชาชน / Passport"
    : lang === "en"
    ? "Tax ID"
    : "เลขผู้เสียภาษี";

  const taxPlaceholder = isPersonal
    ? lang === "en"
      ? "13 digits or passport number"
      : "13 หลัก หรือ Passport"
    : lang === "en"
    ? "13 digits"
    : "13 หลัก";

  const placeholderSelect = lang === "en" ? "-- Select --" : "-- เลือก --";
  const placeholderSearch = lang === "en" ? "Search..." : "ค้นหา...";
  const noOptionsMsg = () => (lang === "en" ? "No results" : "ไม่พบข้อมูล");

  return (
    <Form onSubmit={handleSubmit(handleFinalSubmit)} noValidate>
      <Header>
        <Title>{lang === "en" ? "Company information" : "ข้อมูลบริษัท"}</Title>
        <Subtitle>
          {lang === "en"
            ? "This information will appear on your invoice."
            : "ข้อมูลนี้จะปรากฏบนใบเสร็จ"}
        </Subtitle>
      </Header>

      <Body>
        <FieldGrid>
          {fields.map((fieldId) => {
            const meta = getFieldMeta(fieldId);
            if (!meta) return null;

            const error = errors[fieldId];
            const fullWidth = meta.type === "textarea";

            return (
              <FieldCol key={fieldId} $fullWidth={fullWidth}>
                <Label htmlFor={`company-${fieldId}`}>
                  {meta.label[lang]}
                  {meta.required && <Required>*</Required>}
                </Label>

                {meta.type === "textarea" ? (
                  <Textarea
                    id={`company-${fieldId}`}
                    rows={2}
                    placeholder={meta.placeholder?.[lang]}
                    aria-invalid={!!error}
                    $hasError={!!error}
                    {...register(fieldId)}
                  />
                ) : (
                  <Input
                    id={`company-${fieldId}`}
                    type={meta.type}
                    placeholder={meta.placeholder?.[lang]}
                    aria-invalid={!!error}
                    $hasError={!!error}
                    {...register(fieldId)}
                  />
                )}

                {error && <ErrorText>{error.message}</ErrorText>}
              </FieldCol>
            );
          })}
        </FieldGrid>

        {showBilling && (
          <BillingSection>
            <SectionTitle>
              {lang === "en" ? "Billing details" : "ข้อมูลสำหรับออกใบเสร็จ"}
            </SectionTitle>

            {/* ─── Detail type ─── */}
            <FieldCol $fullWidth>
              <Label>
                {lang === "en" ? "Type" : "ประเภทผู้สมัคร"}
                <Required>*</Required>
              </Label>
              <Controller
                name="detail_type"
                control={control}
                render={({ field }) => (
                  <RadioGroup>
                    <RadioOption $selected={field.value === "company"}>
                      <input
                        type="radio"
                        value="company"
                        checked={field.value === "company"}
                        onChange={() => field.onChange("company")}
                      />
                      <span>{lang === "en" ? "Company" : "นิติบุคคล"}</span>
                    </RadioOption>
                    <RadioOption $selected={field.value === "personal"}>
                      <input
                        type="radio"
                        value="personal"
                        checked={field.value === "personal"}
                        onChange={() => field.onChange("personal")}
                      />
                      <span>{lang === "en" ? "Personal" : "บุคคลธรรมดา"}</span>
                    </RadioOption>
                  </RadioGroup>
                )}
              />
              {errors.detail_type && (
                <ErrorText>{errors.detail_type.message}</ErrorText>
              )}
            </FieldCol>

            {/* ─── Tax / National ID / Passport ─── */}
            <FieldCol $fullWidth>
              <Label htmlFor="company-tax_number">
                {taxLabel}
                <Required>*</Required>
              </Label>
              <Input
                id="company-tax_number"
                type="text"
                placeholder={taxPlaceholder}
                aria-invalid={!!errors.tax_number}
                $hasError={!!errors.tax_number}
                {...register("tax_number")}
              />
              {/* zod มาก่อน (รูปแบบ) แล้วค่อยแสดงผลจากหลังบ้าน (checksum) */}
              {!errors.tax_number && taxCheck.checking && (
                <HintText>
                  {lang === "en" ? "Checking..." : "กำลังตรวจสอบ..."}
                </HintText>
              )}
              {!errors.tax_number && taxCheck.valid === false && (
                <ErrorText>{taxCheck.message}</ErrorText>
              )}
              {!errors.tax_number && taxCheck.valid === true && (
                <OkText>{lang === "en" ? "Valid" : "เลขถูกต้อง"}</OkText>
              )}
              {errors.tax_number && (
                <ErrorText>{errors.tax_number.message}</ErrorText>
              )}
            </FieldCol>

            {/* ─── Branch (hidden for personal) ─── */}
            {!isPersonal && (
              <>
                <FieldCol $fullWidth>
                  <Label>{lang === "en" ? "Office type" : "ประเภทสำนักงาน"}</Label>
                  <Controller
                    name="is_branch_open"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup>
                        <RadioOption $selected={!field.value}>
                          <input
                            type="radio"
                            checked={!field.value}
                            onChange={() => field.onChange(false)}
                          />
                          <span>{lang === "en" ? "Head office" : "สำนักงานใหญ่"}</span>
                        </RadioOption>
                        <RadioOption $selected={field.value}>
                          <input
                            type="radio"
                            checked={field.value}
                            onChange={() => field.onChange(true)}
                          />
                          <span>{lang === "en" ? "Branch" : "สาขา"}</span>
                        </RadioOption>
                      </RadioGroup>
                    )}
                  />
                </FieldCol>

                {isBranchOpen && (
                  <FieldCol $fullWidth>
                    <Label htmlFor="company-name_branch">
                      {lang === "en" ? "Branch number" : "เลขที่สาขา"}
                      <Required>*</Required>
                    </Label>
                    <Input
                      id="company-name_branch"
                      type="text"
                      maxLength={5}
                      placeholder={
                        lang === "en"
                          ? "5 digits, e.g. 00123"
                          : "5 หลัก เช่น 00123"
                      }
                      aria-invalid={!!errors.name_branch}
                      $hasError={!!errors.name_branch}
                      {...register("name_branch")}
                    />
                    {errors.name_branch && (
                      <ErrorText>{errors.name_branch.message}</ErrorText>
                    )}
                  </FieldCol>
                )}
              </>
            )}

            {/* ─── Address group ─── */}
            <Divider />

            <FieldCol $fullWidth>
              <Label htmlFor="company-address2">
                {lang === "en" ? "Address line 2" : "ที่อยู่เพิ่มเติม"}
              </Label>
              <Input
                id="company-address2"
                type="text"
                placeholder={
                  lang === "en"
                    ? "Building, floor, unit (optional)"
                    : "อาคาร, ชั้น, ห้อง (ถ้ามี)"
                }
                {...register("address2")}
              />
            </FieldCol>

            {/* Country */}
            <FieldCol $fullWidth>
              <Label htmlFor="company-id_country">
                {lang === "en" ? "Country" : "ประเทศ"}
                <Required>*</Required>
              </Label>
              <Controller
                name="id_country"
                control={control}
                render={({ field }) => (
                  <Select
                    inputId="company-id_country"
                    options={countryOptions}
                    value={findRsOption(countryOptions, field.value)}
                    onChange={(opt) => field.onChange(opt?.value ?? "")}
                    onBlur={field.onBlur}
                    isLoading={country.isLoading}
                    isClearable={false}
                    isSearchable
                    placeholder={placeholderSearch}
                    noOptionsMessage={noOptionsMsg}
                    styles={selectStyles(!!errors.id_country)}
                    menuPlacement="auto"
                  />
                )}
              />
              {errors.id_country && (
                <ErrorText>{errors.id_country.message}</ErrorText>
              )}
            </FieldCol>

            {/* Province / District */}
            <FieldRow>
              <FieldCol>
                <Label htmlFor="company-id_province">
                  {lang === "en" ? "Province" : "จังหวัด"}
                  {isThailand && <Required>*</Required>}
                </Label>
                <Controller
                  name="id_province"
                  control={control}
                  render={({ field }) => (
                    <Select
                      inputId="company-id_province"
                      options={provinceOptions}
                      value={findRsOption(provinceOptions, field.value)}
                      onChange={(opt) => field.onChange(opt?.value ?? "")}
                      onBlur={field.onBlur}
                      isLoading={province.isLoading}
                      isDisabled={!isThailand || province.isLoading}
                      isClearable
                      isSearchable
                      placeholder={placeholderSelect}
                      noOptionsMessage={noOptionsMsg}
                      styles={selectStyles(!!errors.id_province)}
                      menuPlacement="auto"
                    />
                  )}
                />
                {errors.id_province && (
                  <ErrorText>{errors.id_province.message}</ErrorText>
                )}
              </FieldCol>

              <FieldCol>
                <Label htmlFor="company-id_district">
                  {lang === "en" ? "District" : "อำเภอ/เขต"}
                  {isThailand && <Required>*</Required>}
                </Label>
                <Controller
                  name="id_district"
                  control={control}
                  render={({ field }) => (
                    <Select
                      inputId="company-id_district"
                      options={districtOptions}
                      value={findRsOption(districtOptions, field.value)}
                      onChange={(opt) => field.onChange(opt?.value ?? "")}
                      onBlur={field.onBlur}
                      isLoading={district.isLoading}
                      isDisabled={!isThailand || !idProvince || district.isLoading}
                      isClearable
                      isSearchable
                      placeholder={placeholderSelect}
                      noOptionsMessage={noOptionsMsg}
                      styles={selectStyles(!!errors.id_district)}
                      menuPlacement="auto"
                    />
                  )}
                />
                {errors.id_district && (
                  <ErrorText>{errors.id_district.message}</ErrorText>
                )}
              </FieldCol>
            </FieldRow>

            {/* Subdistrict / Postal */}
            <FieldRow>
              <FieldCol>
                <Label htmlFor="company-id_subdistrict">
                  {lang === "en" ? "Sub-district" : "ตำบล/แขวง"}
                  {isThailand && <Required>*</Required>}
                </Label>
                <Controller
                  name="id_subdistrict"
                  control={control}
                  render={({ field }) => (
                    <Select
                      inputId="company-id_subdistrict"
                      options={subdistrictOptions}
                      value={findRsOption(subdistrictOptions, field.value)}
                      onChange={(opt) => field.onChange(opt?.value ?? "")}
                      onBlur={field.onBlur}
                      isLoading={subDistrict.isLoading}
                      isDisabled={!isThailand || !idDistrict || subDistrict.isLoading}
                      isClearable
                      isSearchable
                      placeholder={placeholderSelect}
                      noOptionsMessage={noOptionsMsg}
                      styles={selectStyles(!!errors.id_subdistrict)}
                      menuPlacement="auto"
                    />
                  )}
                />
                {errors.id_subdistrict && (
                  <ErrorText>{errors.id_subdistrict.message}</ErrorText>
                )}
              </FieldCol>

              <FieldCol>
                <Label htmlFor="company-postal_code">
                  {lang === "en" ? "Postal code" : "รหัสไปรษณีย์"}
                  {isThailand && <Required>*</Required>}
                </Label>
                <Input
                  id="company-postal_code"
                  type="text"
                  readOnly
                  placeholder={lang === "en" ? "Auto-fill" : "เติมอัตโนมัติ"}
                  aria-invalid={!!errors.postal_code}
                  $hasError={!!errors.postal_code}
                  disabled={!isThailand}
                  {...register("postal_code")}
                />
                {errors.postal_code && (
                  <ErrorText>{errors.postal_code.message}</ErrorText>
                )}
              </FieldCol>
            </FieldRow>
          </BillingSection>
        )}

        {/* <Hint>
          {lang === "en"
            ? "Other details can be filled in later from Settings."
            : "ข้อมูลอื่นกรอกได้ในหน้า Settings หลัง login"}
        </Hint> */}
      </Body>

      <Footer>
        {billingCheck.error && (
          <FooterError>
            {billingCheck.error.message}
          </FooterError>
        )}
        {billingCheck.networkError && !billingCheck.error && (
          <FooterError>
            {lang === "en"
              ? "Could not verify with server. Please try again."
              : "ไม่สามารถตรวจสอบข้อมูลกับเซิร์ฟเวอร์ได้ กรุณาลองอีกครั้ง"}
          </FooterError>
        )}
        <PrimaryButton
          type="submit"
          disabled={isSubmitting || billingCheck.loading}
        >
          {billingCheck.loading
            ? lang === "en"
              ? "Checking…"
              : "กำลังตรวจสอบ…"
            : lang === "en"
            ? "Next →"
            : "ถัดไป →"}
        </PrimaryButton>
      </Footer>
    </Form>
  );
}

export default CompanyStep;

// ─── Styled ──────────────────────────────────

const Form = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  padding: 24px 28px 12px;
`;

const Title = styled.h5`
  && {
    font-weight: 700;
    font-size: 18px;
    color: #111827;
    margin: 0 0 4px 0;
  }
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const Body = styled.div`
  padding: 12px 28px 16px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldCol = styled.div`
  ${({ $fullWidth }) => $fullWidth && "grid-column: 1 / -1;"}
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  && {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 4px;
    display: block;
  }
`;

const Required = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const baseInputStyle = `
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  ${baseInputStyle}
  height: 40px;

  ${({ $hasError }) =>
    $hasError &&
    `
    border-color: #ef4444;
    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const Textarea = styled.textarea`
  ${baseInputStyle}
  resize: vertical;
  min-height: 60px;

  ${({ $hasError }) =>
    $hasError &&
    `
    border-color: #ef4444;
    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 11px;
  margin-top: 4px;
`;

/* ข้อความสถานะระหว่างตรวจเลขผู้เสียภาษี — ขนาดเท่า ErrorText เพื่อไม่ให้ layout ขยับ */
const HintText = styled.div`
  color: #9ca3af;
  font-size: 11px;
  margin-top: 4px;
`;

const OkText = styled.div`
  color: #10b981;
  font-size: 11px;
  margin-top: 4px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 16px;
`;

const BillingSection = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px dashed #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 4px;
`;

const Divider = styled.div`
  height: 1px;
  background: #f3f4f6;
  margin: 6px 0;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const RadioOption = styled.label`
  && {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border: 1px solid ${({ $selected }) => ($selected ? "#6366f1" : "#d1d5db")};
    background: ${({ $selected }) => ($selected ? "#eef2ff" : "#ffffff")};
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: ${({ $selected }) => ($selected ? "#4338ca" : "#374151")};
    transition: all 0.15s;
    margin: 0;
    white-space: nowrap;

    &:hover {
      border-color: ${({ $selected }) => ($selected ? "#6366f1" : "#9ca3af")};
    }

    input[type="radio"] {
      accent-color: #6366f1;
      cursor: pointer;
      margin: 0;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 16px 28px;
  border-top: 1px solid #f1f3f5;
  background: #ffffff;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: stretch;
  }
`;

const FooterError = styled.div`
  flex: 1;
  font-size: 12px;
  color: #ef4444;
  text-align: right;

  @media (max-width: 768px) {
    flex: 1 1 100%;
    text-align: left;
  }
`;

const PrimaryButton = styled.button`
  && {
    height: 40px;
    padding: 0 20px;
    background: #6366f1;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, opacity 0.15s;

    &:hover:not(:disabled) {
      background: #4f46e5;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;